import React, { useMemo } from 'react';
import clsx from 'clsx';
import type { TranslationLanguages } from 'stream-chat';
import { Attachment as DefaultAttachment, Avatar as DefaultAvatar, renderText as defaultRenderText, Poll, MessageContextValue, useChannelActionContext, useChatContext, useComponentContext, useMessageContext, useTranslationContext, useUserHandler } from 'stream-chat-react';
import { useAuth } from '../../../hooks/useAuth';
import { authorColor } from '../../../utils/Utils';


export type CustomQuotedMessageProps = Pick<MessageContextValue, 'renderText'>;

export const CustomQuotedMessage = ({ renderText: propsRenderText }: CustomQuotedMessageProps) => {
  const { Attachment = DefaultAttachment } =
    useComponentContext('QuotedMessage');
  const { client } = useChatContext();
  const {
    isMyMessage,
    message,
    renderText: contextRenderText,
  } = useMessageContext('QuotedMessage');
  const { userLanguage } = useTranslationContext('QuotedMessage');
  const { jumpToMessage } = useChannelActionContext('QuotedMessage');
  const {currentUserId} = useAuth();

  const renderText = propsRenderText ?? contextRenderText ?? defaultRenderText;

  const { quoted_message } = message;

  const poll = quoted_message?.poll_id && client.polls.fromState(quoted_message.poll_id);
  const quotedMessageDeleted =
    quoted_message?.deleted_at || quoted_message?.type === 'deleted';

  const quotedMessageText = quotedMessageDeleted
    ? 'This message was deleted...'
    : quoted_message?.i18n?.[`${userLanguage}_text` as `${TranslationLanguages}_text`] ||
      quoted_message?.text;

  const quotedMessageAttachment =
    quoted_message?.attachments?.length && !quotedMessageDeleted
      ? quoted_message.attachments[0]
      : null;

  const renderedText = useMemo(
    () => renderText(quotedMessageText, quoted_message?.mentioned_users),
    [quotedMessageText, quoted_message?.mentioned_users, renderText],
  );

  if (!quoted_message) return null;
  if (!quoted_message.poll && !quotedMessageText && !quotedMessageAttachment) return null;

  const quotedMine = quoted_message.user?.id == currentUserId;

  return (
    <>
      <div
        className={clsx('str-chat__quoted-message-preview', { mine: isMyMessage(), quotedMine: quotedMine })}
        data-testid='quoted-message'
        onClickCapture={(e) => {
          e.stopPropagation();
          e.preventDefault();
          jumpToMessage(quoted_message.id);
        }}
        style={{
          "--user-color": quotedMine ? 'var(--brand-blue)' : authorColor(quoted_message?.user?.id ?? '') 
        } as React.CSSProperties}
      >
        <div
          className='str-chat__quoted-message-bubble'
          data-testid='quoted-message-contents'
        >
            <div className='custom-str-chat__quoted-message-name font-semibold text-sm'>{quoted_message.user?.name}</div>
          {poll ? (
            <Poll isQuoted poll={poll} />
          ) : (
            <>
              {quotedMessageAttachment && (
                <Attachment attachments={[quotedMessageAttachment]} isQuoted />
              )}
              <div
                className='str-chat__quoted-message-bubble__text text-brand-color-2 text-sm'
                data-testid='quoted-message-text'
              >
                {renderedText}
              </div>
            </>
          )}
        </div>
      </div>
      {message.attachments?.length ? (
        <Attachment attachments={message.attachments} />
      ) : null}
    </>
  );
};